import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, map, tap, take, filter, timeout, catchError, of, combineLatest } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { routeMatchesAccountOnlyPath } from './account-only-app-paths';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  private router = inject(Router);
  private authService = inject(AuthService);

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    console.log('🛡️ AuthGuard: Checking authentication and registration...');

    // Wait for auth state, user registration, and business info to be determined
    return combineLatest([
      this.authService.isAuthenticated$,
      this.authService.isRegistered$,
      this.authService.hasBusinessInfo$,
    ]).pipe(
      filter(
        ([isAuthenticated, isRegistered, hasBusinessInfo]) =>
          isAuthenticated !== null && isRegistered !== null && hasBusinessInfo !== null
      ),
      take(1),
      timeout(5000),
      tap(([isAuthenticated, isRegistered, hasBusinessInfo]) =>
        console.log('🛡️ AuthGuard:', { isAuthenticated, isRegistered, hasBusinessInfo })
      ),
      map(([isAuthenticated, isRegistered, hasBusinessInfo]) => {
        if (!isAuthenticated) {
          console.log('🛡️ AuthGuard: Not authenticated, redirecting to index');
          return this.router.createUrlTree(['/index']);
        }

        if (!isRegistered) {
          console.log('🛡️ AuthGuard: User profile not registered, redirecting to user registration');
          return this.router.createUrlTree(['/user-registration']);
        }

        if (!hasBusinessInfo) {
          if (routeMatchesAccountOnlyPath(route)) {
            console.log('🛡️ AuthGuard: No business yet — allowing account-only route');
            return true;
          }
          console.log(
            '🛡️ AuthGuard: Business info not complete — redirecting to profile (not business registration)'
          );
          return this.router.createUrlTree(['/app/profile']);
        }

        console.log('🛡️ AuthGuard: All checks passed, allowing access');
        return true;
      }),
      catchError((error) => {
        console.warn('🛡️ AuthGuard: Timeout or error, checking manually:', error);
        const user = this.authService.getCurrentUser();
        if (!user) {
          return of(this.router.createUrlTree(['/index']));
        }
        return of(this.router.createUrlTree(['/user-registration']));
      })
    );
  }
}
